import React from "react";
import {
  useUnits,
  creatinineDisplay,
  creatinineUnitLabel,
  CREAT_US_TO_SI,
} from "../context/UnitsContext";
import { ArrowUp, ArrowDown, Minus } from "lucide-react";

// For each parameter: `worseWhen` = 'up' means an increase is clinically bad.
// For USG (urine concentrating ability), a decrease is bad.
const PARAMS = [
  { key: "creatinine_mg_dl", label: "Creatinine", unitFn: creatinineUnitLabel, worseWhen: "up", precision: 2 },
  { key: "sdma_ug_dl", label: "SDMA", unit: "µg/dL", worseWhen: "up", precision: 1 },
  { key: "usg", label: "USG", unit: "", worseWhen: "down", precision: 3 },
  { key: "upc", label: "UPC", unit: "", worseWhen: "up", precision: 2 },
  { key: "bp_systolic", label: "Systolic BP", unit: "mmHg", worseWhen: "up", precision: 0 },
];

function toDisplay(param, raw, units) {
  if (raw == null || isNaN(raw)) return null;
  if (param.key === "creatinine_mg_dl") {
    return units === "SI" ? raw * CREAT_US_TO_SI : raw;
  }
  return raw;
}

function fmt(v, p) {
  if (v == null) return "—";
  return Number(v).toFixed(p);
}

function Delta({ prev, curr, worseWhen, precision }) {
  if (prev == null || curr == null) return null;
  const diff = curr - prev;
  const abs = Math.abs(diff);
  // Consider changes below one decimal at precision as stable
  const epsilon = Math.pow(10, -Math.max(precision - 1, 0)) * 0.5;
  if (abs < epsilon) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" data-testid="delta-stable">
        <Minus size={12} /> stable
      </span>
    );
  }
  const isUp = diff > 0;
  const worse = (worseWhen === "up" && isUp) || (worseWhen === "down" && !isUp);
  const cls = worse ? "text-destructive" : "text-[#3f6a52]";
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-medium ${cls}`}
      data-testid={worse ? "delta-worse" : "delta-better"}
    >
      {isUp ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
      {(isUp ? "+" : "-") + fmt(abs, precision)}
    </span>
  );
}

export default function LabComparison({ labs }) {
  const { units } = useUnits();
  if (!labs || labs.length < 2) return null;

  const sorted = [...labs].sort((a, b) => b.date.localeCompare(a.date));
  const last3 = sorted.slice(0, 3); // newest → oldest

  return (
    <div className="mt-10" data-testid="lab-comparison">
      <h3 className="font-display text-2xl">Last {last3.length} visits</h3>
      <p className="text-sm text-muted-foreground mt-1 mb-4">
        Side-by-side comparison with change indicators — arrows show clinical direction
        (red = worse, green = better).
      </p>
      <div className="card-soft overflow-hidden">
        <div className="grid grid-cols-[1.2fr_repeat(3,minmax(0,1fr))] text-sm">
          <div className="px-5 py-4 font-medium text-muted-foreground bg-secondary/40" />
          {last3.map((l, idx) => (
            <div
              key={l.id}
              className="px-5 py-4 font-medium bg-secondary/40 border-l border-border/70"
              data-testid={`comparison-col-header-${idx}`}
            >
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                {idx === 0 ? "Most recent" : idx === 1 ? "Previous" : "Prior"}
              </div>
              <div className="mt-0.5 font-display">{l.date}</div>
            </div>
          ))}

          {PARAMS.map((p, rowIdx) => {
            const values = last3.map((l) => toDisplay(p, l[p.key], units));
            const unitLabel = p.unitFn ? p.unitFn(units) : p.unit;
            if (values.every((v) => v == null)) return null;
            return (
              <React.Fragment key={p.key}>
                <div
                  className={`px-5 py-4 border-t border-border/70 ${rowIdx % 2 === 1 ? "bg-secondary/20" : ""}`}
                >
                  <div className="font-medium">{p.label}</div>
                  {unitLabel && <div className="text-xs text-muted-foreground mt-0.5">{unitLabel}</div>}
                </div>
                {values.map((v, i) => {
                  const prev = values[i + 1]; // older
                  return (
                    <div
                      key={i}
                      className={`px-5 py-4 border-t border-l border-border/70 ${
                        rowIdx % 2 === 1 ? "bg-secondary/20" : ""
                      }`}
                      data-testid={`comparison-cell-${p.key}-${i}`}
                    >
                      <div className="font-display text-base">
                        {v == null ? <span className="text-muted-foreground">—</span> : fmt(v, p.precision)}
                      </div>
                      <div className="mt-1">
                        <Delta prev={prev} curr={v} worseWhen={p.worseWhen} precision={p.precision} />
                      </div>
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}
